import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import { injectIntl, defineMessages } from 'react-intl';
import { withFormik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { withAccount } from '../../components/AccountContext';
import { withGrowl } from '../../components/GrowlNotifications';
import { intlShape, historyShape, growlShape } from '../../components/prop-types';
import PageNotFound from '../../components/PageNotFound';
import { Loading } from '../../components/Loading';
import Debug from '../../components/Debug';
import { validationMessages, notificationMessages } from '../../i18n/common';
import { InputTextWithValidationMessage } from '../../components/prime-formik';
import { getSocialMediaLinkValue } from '../../services/apps/AccountService';
import UploadLogoContainer from './UploadLogoContainer';
import CustomColorPicker from '../../components/CustomColorPicker';

const getValidHexColorCode = (primaryColor) => {
  if (primaryColor.startsWith('#')) {
    return primaryColor;
  }

  return `#${primaryColor}`;
};

const getWebsiteValue = (website) => {
  let value = '';

  if (website) {
    value = website;
  }

  return value;
};

const messages = defineMessages({
  primaryColor: { id: 'attrs.primaryColor', defaultMessage: 'Primary Color' },
  logoWidth: { id: 'attrs.logoWidth', defaultMessage: 'Logo Width' },
  logoHeight: { id: 'attrs.logoHeight', defaultMessage: 'Logo Height' },
  website: { id: 'attrs.website', defaultMessage: 'Website' },
  linkedIn: { id: 'attrs.linkedIn', defaultMessage: 'LinkedIn' },
  facebook: { id: 'attrs.facebook', defaultMessage: 'Facebook' },
  twitter: { id: 'attrs.twitter', defaultMessage: 'Twitter' },
  googlePlus: { id: 'attrs.googlePlus', defaultMessage: 'Google+' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  updateBrandingData: {
    id: 'accounts.update-branding-details.heading',
    defaultMessage: 'Update Branding Details',
  },
});

const UPDATE_BRANDING_DATA = gql`
  mutation updateBrandingInformation($input: UpdateBrandingInformationInput!) {
    updateBrandingInformation(input: $input) {
      account {
        id,
        brandingInformation {
          primaryColor,
          logo {
              width,
              height
          },
          website,
          socialMediaLinks {
              name,
              url
          }
        }
      }
    }
  }
`;

const ACCOUNT_QUERY = gql`
  query querySingleAccountById($accountId: ID!) {
    account(id: $accountId) {
      id
      brandingInformation {
          primaryColor,
          logo {
              url
              width,
              height
          },
          website,
          socialMediaLinks {
              name,
              url
          }
      }
    }
  }
`;

function UpdateBrandingInformationForm(props) {
  const {
    currentAccount,
    intl: { formatMessage },
    toggleEdit,
  } = props;

  return (
    <Form>
      <input type="hidden" name="account" value={currentAccount} />
      <div className="p-grid">
        <div className="p-grid p-col-6">
          <div className="p-col-12">
            <Field
              id="logoWidth"
              name="logoWidth"
              type="number"
              labelText={formatMessage(messages.logoWidth)}
              component={InputTextWithValidationMessage}
            />
          </div>
          <div className="p-col-12">
            <Field
              id="logoHeight"
              name="logoHeight"
              type="number"
              labelText={formatMessage(messages.logoHeight)}
              component={InputTextWithValidationMessage}
            />
          </div>
          <div className="p-col-12">
            <Field
              id="website"
              name="website"
              type="text"
              labelText={formatMessage(messages.website)}
              component={InputTextWithValidationMessage}
            />
          </div>
          <div className="p-col-12">
            <Field
              id="linkedIn"
              name="linkedIn"
              type="text"
              labelText={formatMessage(messages.linkedIn)}
              component={InputTextWithValidationMessage}
            />
          </div>
          <div className="p-col-12">
            <Field
              id="facebook"
              name="facebook"
              type="text"
              labelText={formatMessage(messages.facebook)}
              component={InputTextWithValidationMessage}
            />
          </div>
          <div className="p-col-12">
            <Field
              id="twitter"
              name="twitter"
              type="text"
              labelText={formatMessage(messages.twitter)}
              component={InputTextWithValidationMessage}
            />
          </div>
          <div className="p-col-12">
            <Field
              id="googlePlus"
              name="googlePlus"
              type="text"
              labelText={formatMessage(messages.googlePlus)}
              component={InputTextWithValidationMessage}
            />
          </div>
          <div className="p-col-12">
            <Button
              type="button"
              className="p-button-secondary"
              label={formatMessage(messages.cancel)}
              onClick={toggleEdit}
            />
            <Button type="submit" label={formatMessage(messages.submit)} />
          </div>
        </div>
        <div className="p-grid p-col-6">
          <div className="p-col-12">
            <Field
              id="primaryColor"
              name="primaryColor"
              type="text"
              labelText={formatMessage(messages.primaryColor)}
              component={CustomColorPicker}
            />
            <UploadLogoContainer {...props} />
          </div>
        </div>
      </div>
      <Debug />
    </Form>
  );
}

UpdateBrandingInformationForm.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  toggleEdit: PropTypes.func.isRequired,
};

async function handleSubmit(values, { props, setSubmitting }) {
  const {
    updateBrandingInformationMutation,
    growl,
    toggleEdit,
    intl: { formatMessage },
  } = props;

  const {
    account,
    primaryColor,
    logoWidth,
    logoHeight,
    website,
    linkedIn,
    facebook,
    twitter,
    googlePlus,
  } = values;

  const validHexColorCode = getValidHexColorCode(primaryColor);

  try {
    await updateBrandingInformationMutation({
      variables: {
        input: {
          account,
          brandingInformation: {
            primaryColor: validHexColorCode,
            logoInput: {
              width: logoWidth,
              height: logoHeight,
            },
            website,
            socialMediaLinksInput: [
              {
                name: 'linkedin',
                url: linkedIn,
              },
              {
                name: 'facebook',
                url: facebook,
              },
              {
                name: 'twitter',
                url: twitter,
              },
              {
                name: 'googleplus',
                url: googlePlus,
              },
            ],
          },
        },
      },
    });
    setSubmitting(false);
    growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
    toggleEdit('brandingData');
  } catch (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }
}

function buildValidationSchema(formatMessage) {
  return Yup.object().shape({
    account: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    primaryColor: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    logoWidth: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    logoHeight: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
  });
}

function mapPropsToValues({ intl: { formatMessage }, currentAccount, data }) {
  // Formik requires initial values for each field, to support error messages on submit
  const validationSchema = buildValidationSchema(formatMessage);

  const { brandingInformation } = data.account;

  const links = brandingInformation.socialMediaLinks;

  const linkedInValue = getSocialMediaLinkValue(links, 'linkedin');

  const facebookValue = getSocialMediaLinkValue(links, 'facebook');

  const twitterValue = getSocialMediaLinkValue(links, 'twitter');

  const googlePlusValue = getSocialMediaLinkValue(links, 'googleplus');

  const websiteValue = getWebsiteValue(data.account.brandingInformation.website);

  const initialValues = {
    ...validationSchema.cast(),
    account: currentAccount,
    primaryColor: data.account.brandingInformation.primaryColor,
    logoWidth: data.account.brandingInformation.logo.width,
    logoHeight: data.account.brandingInformation.logo.height,
    website: websiteValue,
    linkedIn: linkedInValue,
    facebook: facebookValue,
    twitter: twitterValue,
    googlePlus: googlePlusValue,
  };
  return initialValues;
}

const UpdateBrandingInformationFormEnhancedForm = withFormik({
  validationSchema: ({ intl: { formatMessage } }) => buildValidationSchema(formatMessage),
  mapPropsToValues,
  handleSubmit,
})(UpdateBrandingInformationForm);

function UpdateBrandingInformation(props) {
  const {
    intl: { formatMessage },
    growl,
    currentAccount,
  } = props;

  const { loading, error, data } = useQuery(ACCOUNT_QUERY, {
    variables: {
      accountId: currentAccount,
    },
  });

  const [updateBrandingInformationMutation] = useMutation(UPDATE_BRANDING_DATA, {
    refetchQueries: ['querySingleAccountById'],
  });

  if (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }

  if (loading) {
    return <Loading />;
  }

  if (data == null && data.account == null) {
    return <PageNotFound />;
  }

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <Panel header={formatMessage(messages.updateBrandingData)}>
          <UpdateBrandingInformationFormEnhancedForm
            updateBrandingInformationMutation={updateBrandingInformationMutation}
            data={data}
            {...props}
          />
        </Panel>
      </div>
    </div>
  );
}

UpdateBrandingInformation.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  history: historyShape.isRequired,
  growl: growlShape.isRequired,
};

export default withAccount(injectIntl(withRouter(withGrowl(UpdateBrandingInformation))));
